import EventEmitter from "eventemitter3";
import { appUtils, IPoint } from "..";

export enum SwiperEvents {
    None = "none",
    Swiping = "swiping",
    SwipeStart = "swipestart",
    SwipeEnd = "swipeend",
}

export enum SwipeDirs {
    None = "none",
    Left = "left",
    Right = "right",
    Up = "up",
    Down = "down",
}

export type SwiperEventObj = {
    event: SwiperEvents,
    dir: SwipeDirs;
    diff: IPoint;
    current: IPoint;
    velocity: IPoint;
}

export type SwiperConfig = {
    threshold?: number, thresholdTime?: number, cursorHand?: boolean,
    mouseLeaveEvent?: boolean, onlyTouch?: boolean
};

export default class Swiper extends EventEmitter<SwiperEvents>{
    private _target: HTMLElement;
    private _scrollElm: HTMLElement;
    private _events: { [name: string]: (this: HTMLElement | Window, ev: MouseEvent | TouchEvent) => void } = {};
    private _mouseDown: IPoint;
    private _downTime: number;
    private _config: SwiperConfig;
    private _prevMouse: IPoint;
    private _prevVelocity: IPoint;
    private _enabled: boolean;

    get enabled() { return this._enabled; }
    set enabled(v: boolean) {
        this._enabled = v;
        if (this._enabled)
            this._target.classList.add("swiper-grab");
        else
            this._target.classList.remove("swiper-grab");
    }

    constructor(target: HTMLElement, config?: SwiperConfig) {
        super();

        this._target = target;
        this._config = { threshold: 50, thresholdTime: -1, cursorHand: true, mouseLeaveEvent: true, ...config };
        this._setEvents();
        this.enabled = true;
    }

    destroy() {
        this.enabled = false;
        this._setEvents(true);
    }

    setActiveScrollContainer(scrollElm: HTMLElement) {
        this._scrollElm = scrollElm;
    }

    private _setEvents(remove: boolean = false) {
        if (remove) {
            this._target.removeEventListener("touchstart", this._events["touchstart"]);
            window.removeEventListener("touchend", this._events["touchend"]);
            window.removeEventListener("touchcancel", this._events["touchcancel"]);
            window.removeEventListener("touchmove", this._events["touchmove"]);

            if (this._config.onlyTouch)
                return;

            this._target.removeEventListener("mousedown", this._events["mousedown"]);
            window.removeEventListener("mouseup", this._events["mouseup"]);
            window.removeEventListener("mousemove", this._events["mousemove"]);

            if (this._config.mouseLeaveEvent)
                document.body.removeEventListener("mouseleave", this._events["mouseleave"]);
        } else {
            this._target.addEventListener("touchstart", this._events["touchstart"] = this._onMouseDown.bind(this));
            window.addEventListener("touchend", this._events["touchend"] = this._onMouseUp.bind(this));
            window.addEventListener("touchcancel", this._events["touchcancel"] = this._onMouseUp.bind(this));
            window.addEventListener("touchmove", this._events["touchmove"] = this._onMouseMove.bind(this));

            if (this._config.onlyTouch)
                return;

            this._target.addEventListener("mousedown", this._events["mousedown"] = this._onMouseDown.bind(this));
            window.addEventListener("mouseup", this._events["mouseup"] = this._onMouseUp.bind(this));
            window.addEventListener("mousemove", this._events["mousemove"] = this._onMouseMove.bind(this));

            if (this._config.mouseLeaveEvent)
                document.body.addEventListener("mouseleave", this._events["mouseleave"] = this._onMouseUp.bind(this));
        }
    }

    private _onMouseDown(e: MouseEvent | TouchEvent) {
        if (!this.enabled)
            return;

        if (this._config.cursorHand == true)
            this._target.style.cursor = "grabbing";

        this._downTime = new Date().getTime();
        this._prevMouse = this._mouseDown = appUtils.mouseTouchXY(e);
        this._prevVelocity = { x: 0, y: 0 };
        this.emit(SwiperEvents.SwipeStart, this._getEventObj(SwiperEvents.SwipeStart, this._mouseDown, this._prevMouse, this._mouseDown));

        if (e instanceof MouseEvent)
            e.preventDefault();
    }

    private _onMouseMove(e: MouseEvent | TouchEvent) {
        if (!this._mouseDown)
            return;

        const eventObj = this._getEventObj(SwiperEvents.Swiping, this._mouseDown, this._prevMouse, appUtils.mouseTouchXY(e));
        this._prevMouse = eventObj.current;
        this._prevVelocity = eventObj.velocity;
        this.emit(SwiperEvents.Swiping, eventObj);
    }

    private _onMouseUp(e: MouseEvent | TouchEvent) {
        const timeDiff = new Date().getTime() - this._downTime;

        if (this._mouseDown && (this._config.thresholdTime == -1 || timeDiff < this._config.thresholdTime))
            this.emit(SwiperEvents.SwipeEnd,
                this._getEventObj(SwiperEvents.SwipeEnd, this._mouseDown, this._prevMouse, appUtils.mouseTouchXY(e)));

        this._mouseDown = null;

        if (this._config.cursorHand == true)
            this._target.style.cursor = "";
    }

    private _getEventObj(event: SwiperEvents, down: IPoint, prev: IPoint, current: IPoint): SwiperEventObj {
        const diff = { x: current.x - this._mouseDown.x, y: current.y - this._mouseDown.y },
            diffAbs = { x: Math.abs(diff.x), y: Math.abs(diff.y) },
            velocity = event == SwiperEvents.SwipeEnd ? this._prevVelocity : { x: current.x - prev.x, y: current.y - prev.y };

        let dir = SwipeDirs.None;

        if ((diffAbs.x > this._config.threshold || diffAbs.y > this._config.threshold)) {
            if (diffAbs.x > diffAbs.y) {
                if (diff.x > 0)
                    dir = SwipeDirs.Left;
                else
                    dir = SwipeDirs.Right;
            } else {
                if (diff.y > 0)
                    dir = SwipeDirs.Up;
                else
                    dir = SwipeDirs.Down;
            }
        }

        if (event == SwiperEvents.SwipeEnd && this._scrollElm &&
            (this._scrollElm.scrollTop > 0 &&
                this._scrollElm.scrollHeight - this._scrollElm.scrollTop > this._scrollElm.offsetHeight + 1)
        ) {
            dir = SwipeDirs.None;
        }

        return { event, dir, diff, current, velocity };
    }
}